import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ROLES } from '../../model/roles';
import useVoucher from '../../hooks/useVoucher';
import useCorsi from '../../hooks/useCorsi';
import VisualizzaVoucherAgenzia from '../../components/VisualizzaVoucherAgenzia/VisualizzaVoucherAgenzia';
import CustomFilterTable from '../../components/CustomFilterTable/CustomFilterTable';
import { Card, Spinner } from 'react-bootstrap';
import { alertService } from '../../services/AlertService';

const VoucherAgenziaPage = () => {
    const userLoggedIn = useSelector(state => state.user?.userData);
    const { getVoucherByIdAgenzia, loadingStateVoucher } = useVoucher();
    const { getCorsiByParams } = useCorsi();

    const [voucherList, setVoucherList] = useState([]);
    const [filteredVoucherList, setFilteredVoucherList] = useState([]);
    const [formDataFilter, setFormDataFilter] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Verifica che l'utente sia un PARTNER con id_agenzia
    useEffect(() => {
        if (userLoggedIn?.role === ROLES.PARTNER && userLoggedIn?.id_agenzia) {
            loadVouchers();
        }
    }, [userLoggedIn]);

    const loadVouchers = async () => {
        if (!userLoggedIn?.id_agenzia) return;
        
        setIsLoading(true);
        try {
            const vouchers = await getVoucherByIdAgenzia(userLoggedIn.id_agenzia);
            setVoucherList(Array.isArray(vouchers) ? vouchers : []);
            setFilteredVoucherList(Array.isArray(vouchers) ? vouchers : []);
        } catch (error) {
            alertService.error("Errore nel caricamento dei voucher.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilter = () => {
        let filtered = [...voucherList];

        // Filtro per corso (nome_corso contiene il testo cercato)
        if (formDataFilter.nome_corso) {
            filtered = filtered.filter(v => 
                v.nome_corso?.toLowerCase().includes(formDataFilter.nome_corso.toLowerCase())
            );
        }

        // Filtro per stato
        if (formDataFilter.stato_voucher) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            filtered = filtered.filter(v => {
                const scadenza = new Date(v.data_scadenza);
                scadenza.setHours(0, 0, 0, 0);
                const isExpired = scadenza < today;
                const isEsaurito = v.esaurito === 1 || v.esaurito === "1";
                
                if (formDataFilter.stato_voucher === 'attivo') {
                    return !isExpired && !isEsaurito;
                } else if (formDataFilter.stato_voucher === 'scaduto') {
                    return isExpired && !isEsaurito;
                } else if (formDataFilter.stato_voucher === 'esaurito') {
                    return isEsaurito;
                }
                return true;
            });
        }

        // Filtro per data scadenza (da)
        if (formDataFilter.data_scadenza_da) {
            const dataDa = new Date(formDataFilter.data_scadenza_da);
            dataDa.setHours(0, 0, 0, 0);
            filtered = filtered.filter(v => {
                const scadenza = new Date(v.data_scadenza);
                scadenza.setHours(0, 0, 0, 0);
                return scadenza >= dataDa;
            });
        }

        // Filtro per data scadenza (a)
        if (formDataFilter.data_scadenza_a) {
            const dataA = new Date(formDataFilter.data_scadenza_a);
            dataA.setHours(23, 59, 59, 999);
            filtered = filtered.filter(v => {
                const scadenza = new Date(v.data_scadenza);
                scadenza.setHours(23, 59, 59, 999);
                return scadenza <= dataA;
            });
        }

        setFilteredVoucherList(filtered);
    };

    const handleSearchCorsi = (params) => getCorsiByParams(params);

    // Configurazione filtri per CustomFilterTable
    const searchNodeType = [
        { 
            nodeType: "input", 
            name: "nome_corso", 
            placeholder: "Nome corso" 
        },
        { 
            nodeType: "select", 
            name: "stato_voucher", 
            placeholder: "Stato voucher" 
        },
        { 
            nodeType: "date", 
            name: "data_scadenza_da", 
            placeholder: "Scadenza da" 
        },
        { 
            nodeType: "date", 
            name: "data_scadenza_a", 
            placeholder: "Scadenza a" 
        }
    ];

    if (userLoggedIn?.role !== ROLES.PARTNER || !userLoggedIn?.id_agenzia) {
        return (
            <div className="card custom-card w-100 shadow p-5">
                <div className="text-center">
                    <h5>Accesso non autorizzato</h5>
                    <p className="text-muted">Solo le agenzie possono visualizzare i voucher.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card w-100 shadow p-3" style={{ minHeight: "700px" }}>
            <div className="container">
                <div className="d-flex justify-content-between custom-card-header">
                    <div className="d-flex gap-3 custom-card-title my-2">
                        <div className="custom-icon-container">
                            <i className="bi custom-icon bi-ticket-perforated fs-2"></i>
                        </div>
                        <div className="align-self-center">
                            <span className="fs-4 d-block">
                                <i><b>Voucher Agenzia</b></i>
                            </span>
                        </div>
                    </div>
                </div>

                <div className="mt-4">
                    <CustomFilterTable
                        searchNodeType={searchNodeType}
                        setFormDataFilter={setFormDataFilter}
                        formDataFilter={formDataFilter}
                        onFilteredTable={handleFilter}
                        isLoadingData={isLoading}
                    />
                </div>

                <div className="mt-4">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Caricamento voucher...</p>
                        </div>
                    ) : (
                        <VisualizzaVoucherAgenzia
                            voucherList={filteredVoucherList}
                            idAgenzia={userLoggedIn.id_agenzia}
                            onRefresh={loadVouchers}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoucherAgenziaPage;
